<template>
  <div
    class="press-releases-actions-container"
    :class="{
      fixed: isFixed || isMobile,
      'is-mobile': isMobile,
      'is-toggled': isMobileMenuToggled,
      'has-transition': hasTransition
    }"
    :style="isFixed ? { marginTop: 0 } : {}">
    <div class="press-releases-filters-container"
         :class="{
      'container': isFixed || isMobile,
      'is-mobile': isMobile,
    }">
      <button
        v-if="isMobile && isMobileMenuToggled"
        class="close-menu-btn"
        @click="handleMenuToggle">
        <i :class="isMobileMenuToggled ? 'icon-cross xs' : 'icon-menu xs'"></i>
      </button>
      <div
        class="press-releases-filters"
        :class="isMobile ? 'is-mobile': ''">
        <Dropdown
          :isFilter="true"
          :isMobile="isMobile"
          :allOptions="{ name: translate('prAllTopics'), key: 'allTopics', isDisabled: false }"
          :title="translate('prTopicFilter')"
          id="topic"
          icon="icon-info-2"
          @select="onSelect($event, 'topic')"
          :options="getTopicDropdownOptions"/>
        <Dropdown
          :isFilter="true"
          :isMobile="isMobile"
          :allOptions="{ name: translate('prAllAreas'), key: 'allAreas', isDisabled: false }"
          :title="translate('prAreaFilter')"
          :link="{
            text: translate('prAmerPRLink'),
            url: translate('prAmerPRLinkURL'),
            icon: 'icon-open-url-icon'
          }"
          id="area"
          icon="icon-world"
          @select="onSelect($event, 'area')"
          :options="getAreaDropdownOptions"/>
        <Dropdown
          :isFilter="true"
          :isMobile="isMobile"
          :allOptions="{ name: translate('prAllYears'), key: 'allYears', isDisabled: false }"
          :title="translate('prYearFilter')"
          id="year"
          icon="icon-calendar"
          @select="onSelect($event, 'year')"
          :options="getYearDropdownOptions"/>
          <button
            v-if="isMobile && isMobileMenuToggled"
            class="save-menu-btn btn btn-primary"
            @click="handleMenuToggle">
            {{ translate('prSave') }}
          </button>
        </div>

      <div class="active-filters-infobox" v-if="areFiltersSelected && !isMobile && !getQueryParamError">
        <Dropdown
          class="page-size-infobox"
          v-if="(getTotalResults > getDefaultPageSize) && !loading"
          :selectedOption="getSelectedPageSize"
          :title="translate('prSee')"
          presettedLabel="Show"
          :isThin="true"
          id="pageSize"
          @select="onSelectPageSize"
          :options="[{ key: 15, name: '15' }, { key: 30, name: '30' }]"/>
        <div
          class="results-infobox"
          :class="isFixed ? 'fixed': ''">
          <p class="results-infobox__number" v-if="!loading">{{ getTotalResults }}</p>
          <p class="results-infobox__text">{{ translatePrFilter(getTotalResults) }}</p>
          <button
            class="btn btn-link results-infobox__clear"
            @click="onClearFilters">
            <i class="icon-cross xs icon-right"></i>{{translate('prClearAllFilters') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { translate } from 'rhg-vue-common'
import Dropdown from '@/components/Dropdown'
import { updateQueryParams, clearQueryParams, getQueryParams, cleanObject, isEmptyObject, capitalize, scrollTop, triggerCustomEvent, removeInvalidParams } from '@/infrastructure/Utils'
import { defaultPressPageSize } from '@/infrastructure/Configuration'

export default {
  name: 'Filters',
  components: {
    Dropdown
  },
  data: () => {
    return {
      hasTransition: true,
      defaultOptions: {
        topic: { name: translate('prAllTopics'), key: 'allTopics', isDisabled: false },
        area: { name: translate('prAllAreas'), key: 'allAreas', isDisabled: false },
        year: { name: translate('prAllYears'), key: 'allYears', isDisabled: false }
      }
    }
  },
  props: {
    isMobile: Boolean,
    isFixed: Boolean
  },
  async mounted () {
    const queryParams = getQueryParams(window.location.search)
    this.triggerQueryParams(queryParams)

    this.hasTransition = this.isMobile

    if (!isEmptyObject(queryParams)) {
      const keys = Object.keys(queryParams).filter(key => Object.keys(this.defaultOptions).includes(key))

      this.$nextTick(() => {
        keys.forEach((key) => {
          const optionKey = this[`get${capitalize(key)}DropdownOptions`].find(option => String(option.key) === queryParams[key])
          if (optionKey === undefined) {
            this.$store.dispatch('setParamsErrorStatus', true)
          }
          this.$store.commit('setSelectedFilters', {
            type: capitalize(key),
            option: optionKey || this.defaultOptions[key]
          })
        })
      })
      this.$nextTick(() => {
        this.$store.dispatch('getPressReleases')
      })
    }

    this.$root.$on('clear-filters', () => this.triggerQueryParams(getQueryParams(window.location.search)))
  },
  methods: {
    triggerQueryParams (queryParams) {
      const cleanedAndValidatedParams = removeInvalidParams(cleanObject(queryParams))
      if (!isEmptyObject(cleanedAndValidatedParams)) {
        const parsedQueryParams = {}

        Object.keys(cleanedAndValidatedParams).forEach((key) => {
          const optionKey = this[`get${capitalize(key)}DropdownOptions`].find(option => String(option.key) === cleanedAndValidatedParams[key])
          if (optionKey) {
            parsedQueryParams[key] = { key: optionKey.key, name: optionKey.name }
          }
        })
        triggerCustomEvent('params-change', parsedQueryParams)
      } else {
        triggerCustomEvent('params-change', {})
      }
    },
    async onSelect (option, type) {
      await this.$store.dispatch('setParamsErrorStatus', false)
      await this.$store.commit('setSelectedFilters', { type: capitalize(type), option })
      const defaultOptions = ['allTopics', 'allAreas', 'allYears']

      defaultOptions.includes(option.key) ? updateQueryParams({}, type) : updateQueryParams(option, type)
      this.triggerQueryParams(getQueryParams(window.location.search))

      await this.$store.commit('setPaginationOffset', 1)
      await this.$store.dispatch('getPressReleases', type)
      scrollTop()
    },
    async onClearFilters () {
      await this.$store.dispatch('setParamsErrorStatus', false)
      await this.$store.commit('clearFilters')
      clearQueryParams()
      this.triggerQueryParams(getQueryParams(window.location.search))
      await this.$store.dispatch('getPressReleases')
      scrollTop()
    },
    onSelectPageSize ({ key }) {
      this.$store.commit('setPaginationLimit', key)
      this.$store.commit('setPaginationOffset', 1)
      this.$store.dispatch('getPressReleases')
    },
    handleMenuToggle () {
      this.$store.commit('toggleMobileMenu')

      this.isMobileMenuToggled ? document.documentElement.style.overflow = 'hidden' : document.documentElement.style.overflow = 'auto'
    },
    translatePrFilter (option) {
      return `${translate('prResult', option, option)}`
    },
    translate
  },
  computed: {
    ...mapState(['topicDropdownOptions', 'areaDropdownOptions', 'areas', 'topics', 'filters', 'loading', 'pressReleases', 'selectedFilters', 'isMobileMenuToggled']),
    ...mapGetters(['getQueryParamError', 'areFiltersSelected', 'getSelectedPageSize', 'getAreaDropdownOptions', 'getTopicDropdownOptions', 'getYearDropdownOptions', 'getTotalResults']),
    getDefaultPageSize: () => defaultPressPageSize
  },
  watch: {
    isMobile (newValue) {
      if (newValue) {
        setTimeout(() => {
          this.hasTransition = true
        }, 1)
      } else {
        this.hasTransition = false
      }
    }
  }

}
</script>

<style lang="scss" scoped>
  .press-releases-actions-container {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    min-height: 66px;
    width: 1140px;
    margin: auto;

    &.fixed {
      background-color: white;
      z-index: 101;
      align-items: center;
      position: fixed;
      top: 0;
      right: 0;
      left: 0;
      padding: 8px 0;
      width: 100%;
    }

    &.has-transition {
      transition: all .6s ease-out;
    }

    &.is-mobile {
      background: rgba(0, 0, 0, 0.4);
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      transform: translate(100vw);
      z-index: 602;
      height: 100vh;
    }

    &.is-toggled {
      transform: translate(0)
    }

    .close-menu-btn {
      width: 40px;
      height: 40px;
      border: none;
      background: transparent;
      color: white;
      border-bottom-left-radius: 4px;
      padding: 0;
      margin: 0;
    }
    .save-menu-btn {
      position: relative;
      margin: 24px 16px 40px;
      width: calc(100% - 32px);
      line-height: 0px;
      padding: 3rem;
    }

    .press-releases-filters-container {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      @media screen and (min-width: 576px) {
        width: 540px;
      }
      @media screen and (min-width: 768px) {
        width: 720px;
      }
      @media screen and (min-width: 992px) {
        width: 960px;
      }
      @media screen and (min-width: 1200px) {
        width: 1140px;
      }

      &.is-mobile {
        background: rgba(0, 0, 0, 0.4);
        flex-direction: row;
        padding-right: 0;
        padding-left: 0;
        width: 100%;
        max-width: 100%;
      }
    }

    .press-releases-filters {
      display: flex;

      &.is-mobile {
        flex-direction: column;
        width: 100%;
        position: relative;
        height: 100vh;
        overflow-y: scroll;
        background-color: white;
        padding-bottom: 8rem;
      }
    }

    .active-filters-infobox {
      display: flex;
      flex-direction: row;

      .results-infobox {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 240px;
        padding: 16px 0;
        background: #F8F8F8;
        border: 1px solid #EEEEEE;
        border-radius: 4px;
        min-height: 66px;
        transition: all 0.3s ease-out;

        &.fixed {
          flex-direction: row;
          align-items: flex-end;
          position: initial;
          width: auto;
          padding: 12px 24px;

          & .results-infobox__number {
            font-size: 40px;
            line-height: 40px;
            font-weight: 100;
            margin-right: 8px;
          }

          & .results-infobox__text {
            margin-right: 16px;
          }
        }

        &__number, &__text {
          margin: 0;
          padding: 0;
          color: #53565A;
        }

        &__number {
          font-size: 60px;
          line-height: 60px;
        }

        &__clear {
          white-space: nowrap;
        }

        button {
          background-color: transparent;
          border: none;
          padding: 0;
          margin: 0;
          outline: none;
          .icon-cross {
            font-size: 11px
          }
        }
      }
    }
  }
</style>
